import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { buildFilter } from '@platform/core/lib/filterDsl';
import { ApiError } from '@platform/dao';
import { useSaveViewMutation, useViewsQuery } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import type { IRecordListModel, PageSize } from '@platform/core/types';
import { kindOfView } from './kindOfView';

/**
 * «Зберегти як подання» панелі розширених фільтрів (прототип v4): нове подання «<поточне подання> · Відфільтровано» (або «Відфільтровано»)
 * з поточними стовпцями, сортуванням, розміром сторінки й фільтром = фільтр поточного подання + умови `advanced`; після збереження
 * подання вибирається, а `onSaved` очищає умови й ховає панель. Збереження йде через API подань (useSaveViewMutation).
 */
export function useSaveAsView(model: IRecordListModel, advanced: string, onSaved: () => void): () => void {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { ws, rt, state, columns, setState } = model;
  const { data: views = [] } = useViewsQuery({ ws, recordType: rt.key });
  const [save] = useSaveViewMutation();
  return useCallback(() => {
    const current = views.find((v) => v.id === state.savedViewId);
    const name = current ? `${current.name} · ${t('listbar.filtered')}` : t('listbar.filtered');
    const definition = { ...current?.definition, columns, sort: state.sort || current?.definition.sort, pageSize: state.pageSize as PageSize, filter: buildFilter([current?.definition.filter, advanced]) };
    save({ ws, body: { recordTypeKey: rt.key, name, kind: current?.kind ?? kindOfView(state.view), definition } }).unwrap()
      .then((view) => {
        setState({ savedViewId: view.id, page: 1 });
        onSaved();
        dispatch(toastShown({ kind: 'success', text: t('listbar.viewSaved', { name: view.name }) }));
      })
      .catch((e: unknown) => dispatch(toastShown({ kind: 'error', text: t('listbar.failed', { message: ApiError.fromUnknown(e).message }) })));
  }, [views, state, columns, advanced, save, ws, rt.key, setState, onSaved, dispatch, t]);
}
